import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Users,
  Target,
  Video,
  Gamepad2,
  FileText,
  Globe,
  Newspaper,
  Loader2,
  Plus,
  Eye,
  Radio,
  HelpCircle
} from 'lucide-react';
import PlatformIcon from '../components/PlatformIcon';
import Creators from './Creators';
import CreatorDetail from './CreatorDetail';
import Campaigns from './Campaigns';
import CampaignDetail from './CampaignDetail';
import Games from './Games';
import Reports from './Reports';
import ClientPortals from './ClientPortals';
import ClientPortalEditor from './ClientPortalEditor';
import PrCampaigns from './PrCampaigns';
import PrCampaignDetail from './PrCampaignDetail';
import UpcomingReleases from './UpcomingReleases';
import Help from './Help';
import ScrollToTop from '../components/ScrollToTop';
import Footer from '../components/Footer';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  // The Meta App Review test account (Instagram API Review org) is restricted to the
  // Instagram test campaign + its report/portal — hide areas it doesn't need so a
  // reviewer never wanders into unrelated sections.
  const REVIEW_ORG_ID = '00f4e0f7-d1fa-46f9-8a7d-f1f95a48f4b7';
  const isReviewer = (user?.organization?.id || user?.organizationId) === REVIEW_ORG_ID;
  const HIDDEN_FOR_REVIEWER = ['Games', 'Upcoming', 'Help', 'PR Campaigns'];
  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: BarChart3 },
    { name: 'Creators', path: '/dashboard/creators', icon: Users },
    { name: 'Campaigns', path: '/dashboard/campaigns', icon: Target },
    { name: 'Games', path: '/dashboard/games', icon: Gamepad2 },
    { name: 'Reports', path: '/dashboard/reports', icon: FileText },
    { name: 'Client Portals', path: '/dashboard/client-portals', icon: Globe },
    { name: 'PR Campaigns', path: '/dashboard/pr-campaigns', icon: Newspaper },
    { name: 'Upcoming', path: '/dashboard/upcoming-releases', icon: Radio },
    { name: 'Help', path: '/dashboard/help', icon: HelpCircle },
  ].filter((item) => !isReviewer || !HIDDEN_FOR_REVIEWER.includes(item.name));

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a14 50%, #0a0a1a 100%)' }}>
      {/* Top Navbar */}
      <div className="bg-gray-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center justify-between px-4 py-2">
          <img
            src="https://streamersconnected.com/wp-content/uploads/2025/04/sc_header_logo.svg"
            alt="Streamers Connected"
            className="h-8 w-auto"
          />
          <nav className="flex items-center gap-0.5">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
                    isActive(item.path)
                      ? 'bg-sc-pink/20 text-sc-pink font-medium border border-sc-pink/25'
                      : 'text-white hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            {user?.organization?.name && (
              <span className="text-[11px] text-white hidden md:inline border border-white px-2 py-0.5 rounded-md">
                {user.organization.name}
              </span>
            )}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-sc-pink/20 border border-sc-pink/30 rounded-full flex items-center justify-center text-sc-pink text-sm font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm text-white hidden sm:inline">{user?.name}</span>
            </div>
            <button
              onClick={logout}
              className="bg-white/5 border border-white text-white px-3 py-1.5 rounded-lg hover:bg-white/10 hover:text-white text-sm font-medium transition-all"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<DashboardHome />} />
          <Route path="/creators" element={<Creators />} />
          <Route path="/creators/:id" element={<CreatorDetail />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/campaigns/:id" element={<CampaignDetail />} />
          <Route path="/games" element={<Games />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/client-portals" element={<ClientPortals />} />
          <Route path="/client-portals/:id" element={<ClientPortalEditor />} />
          <Route path="/pr-campaigns" element={<PrCampaigns />} />
          <Route path="/pr-campaigns/:id" element={<PrCampaignDetail />} />
          <Route path="/upcoming-releases" element={<UpcomingReleases />} />
          <Route path="/help" element={<Help />} />
        </Routes>
        <Footer />
      </div>
      <ScrollToTop />
    </div>
  );
};

// Dashboard Home Component
const DashboardHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/dashboard/stats`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardStats();
  }, []);

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const timeAgo = (dateString) => {
    if (!dateString) return '';
    const diff = Date.now() - new Date(dateString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const stats = data?.stats;
  const statCards = [
    { label: 'Active Campaigns', value: stats?.campaigns || 0, icon: Target, accent: '#ec4899' },
    { label: 'Tracked Creators', value: stats?.creators || 0, icon: Users, accent: '#06b6d4' },
    { label: 'Content This Week', value: stats?.contentThisWeek || 0, icon: Video, accent: '#8b5cf6' },
    { label: 'Total Views', value: formatNumber(stats?.totalViews), icon: Eye, accent: '#10b981' },
  ];

  const getStatusStyle = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/15 text-green-400 border-green-500/25';
      case 'planning': return 'bg-amber-500/15 text-amber-400 border-amber-500/25';
      case 'completed': return 'bg-gray-500/15 text-white border-gray-500/25';
      default: return 'bg-white/10 text-white border-white';
    }
  };

  // Platform breakdown bar
  const platformBreakdown = data?.platformBreakdown || [];
  const totalContent = platformBreakdown.reduce((sum, p) => sum + p.count, 0);
  const platformColors = { youtube: '#ef4444', twitch: '#9333ea', kick: '#00e70a', tiktok: '#06b6d4', twitter: '#3b82f6', instagram: '#f59e0b' };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="flex flex-col items-center">
          <Loader2 size={32} className="animate-spin text-sc-pink" />
          <span className="text-white mt-4 font-medium">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">Dashboard</h2>
          <p className="text-sm text-white">Welcome back, {user?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/dashboard/campaigns')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 transition-all text-sm font-semibold"
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </button>
          <button
            onClick={() => navigate('/dashboard/creators')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-all text-sm font-semibold"
          >
            <Plus className="w-4 h-4" />
            Add Creator
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-gray-900/80 border border-white rounded-xl p-4 relative overflow-hidden">
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-10 blur-xl" style={{ background: card.accent }} />
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-white mb-0.5">{card.label}</p>
                  <p className="text-xl font-bold text-white">{card.value}</p>
                </div>
                <div className="p-2 rounded-lg" style={{ background: `${card.accent}15` }}>
                  <Icon className="w-4 h-4" style={{ color: card.accent }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Platform Breakdown Bar */}
      {totalContent > 0 && (
        <div className="bg-gray-900/60 border border-white rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-white">Content by Platform</span>
            <span className="text-xs text-white">{totalContent} total</span>
          </div>
          <div className="flex rounded-full h-2.5 overflow-hidden bg-white/5">
            {platformBreakdown.map((p) => (
              <div
                key={p.platform}
                className="h-full transition-all"
                style={{
                  width: `${(p.count / totalContent) * 100}%`,
                  background: platformColors[p.platform?.toLowerCase()] || '#6b7280'
                }}
                title={`${{ youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick', tiktok: 'TikTok', instagram: 'Instagram', twitter: 'Twitter' }[p.platform?.toLowerCase()] || p.platform}: ${p.count} (${formatNumber(p.views)} views)`}
              />
            ))}
          </div>
          <div className="flex items-center gap-4 mt-2.5">
            {platformBreakdown.map((p) => (
              <div key={p.platform} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: platformColors[p.platform?.toLowerCase()] || '#6b7280' }} />
                <span className="text-[11px] text-white">{{ youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick', tiktok: 'TikTok', instagram: 'Instagram', twitter: 'Twitter' }[p.platform?.toLowerCase()] || p.platform}</span>
                <span className="text-[11px] text-white">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live Streams + Campaigns Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

        {/* Who's Live Now */}
        <div className="bg-gray-900/80 border border-white rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="relative">
              <Radio className="w-4 h-4 text-red-400" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            </div>
            <span className="text-sm font-semibold text-white">Live Now</span>
            {(data?.totalLiveCount || data?.liveStreams?.length || 0) > 0 && (
              <span className="text-[10px] bg-red-500/15 text-red-400 border border-red-500/20 px-2 py-0.5 rounded-full font-medium">
                {data.totalLiveCount || data.liveStreams.length}
              </span>
            )}
          </div>
          {(!data?.liveStreams || data.liveStreams.length === 0) ? (
            <div className="text-center py-6">
              <Radio className="w-8 h-8 text-white/8 mx-auto mb-2" />
              <p className="text-xs text-white">No one's live right now</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
              {data.liveStreams.map((stream) => (
                <div
                  key={stream.id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all group ${
                    stream.isCampaign
                      ? 'bg-pink-500/5 border-pink-500/15 hover:border-pink-500/30'
                      : 'bg-white/3 border-white hover:border-white hover:bg-white/5'
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <PlatformIcon platform={stream.platform} size="md" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-white truncate">{stream.name}</p>
                      {stream.isCampaign && (
                        <span className="text-[9px] bg-pink-500/20 text-pink-400 px-1.5 py-0.5 rounded font-medium flex-shrink-0">CAMPAIGN</span>
                      )}
                    </div>
                    <p className="text-[11px] text-white truncate">{stream.game || stream.title}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-xs font-semibold text-red-400">{(stream.viewers || 0).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Campaigns */}
        <div className="bg-gray-900/80 border border-white rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-white">Campaigns</span>
            <button
              onClick={() => navigate('/dashboard/campaigns')}
              className="text-[11px] text-white hover:text-white transition-colors"
            >
              View all
            </button>
          </div>
          {(!data?.recentCampaigns || data.recentCampaigns.length === 0) ? (
            <div className="text-center py-6">
              <Target className="w-8 h-8 text-white/8 mx-auto mb-2" />
              <p className="text-xs text-white">No campaigns yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.recentCampaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  onClick={() => navigate(`/dashboard/campaigns/${campaign.id}`)}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-white/3 border border-white hover:border-white hover:bg-white/5 cursor-pointer transition-all group"
                >
                  {campaign.gameImage ? (
                    <img src={campaign.gameImage} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                      <Gamepad2 className="w-4 h-4 text-white/15" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate group-hover:text-sc-pink transition-colors">{campaign.name}</p>
                    <div className="flex items-center gap-2 text-[11px] text-white">
                      <span>{campaign.creatorCount} creators</span>
                      {campaign.startDate && (
                        <>
                          <span className="text-white">|</span>
                          <span>{formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${getStatusStyle(campaign.status)}`}>
                    {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Content + Top Creators Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Recent Content - takes 2 cols */}
        <div className="lg:col-span-2 bg-gray-900/80 border border-white rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-white">Recent Content</span>
          </div>
          {(!data?.recentContent || data.recentContent.length === 0) ? (
            <div className="text-center py-8">
              <Video className="w-8 h-8 text-white/8 mx-auto mb-2" />
              <p className="text-xs text-white">No content tracked yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {data.recentContent.map((content) => (
                <div
                  key={content.id}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-white/3 border border-white hover:border-white hover:bg-white/5 transition-all group"
                >
                  {/* Thumbnail */}
                  {content.thumbnailUrl ? (
                    <img
                      src={content.thumbnailUrl}
                      alt=""
                      className="w-16 h-10 rounded-md object-cover flex-shrink-0 bg-white/5"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-16 h-10 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0">
                      <Video className="w-4 h-4 text-white/10" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{content.title || 'Untitled'}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-white">{content.creator?.name}</span>
                      <span className="text-white">·</span>
                      <div className="flex items-center gap-0.5">
                        <PlatformIcon platform={content.platform} size="xs" />
                      </div>
                      {content.views > 0 && (
                        <>
                          <span className="text-white">·</span>
                          <span className="text-[10px] text-white">{formatNumber(content.views)} views</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-white flex-shrink-0">{timeAgo(content.postDate)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Creators */}
        <div className="bg-gray-900/80 border border-white rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-white">Top Creators</span>
            <button
              onClick={() => navigate('/dashboard/creators')}
              className="text-[11px] text-white hover:text-white transition-colors"
            >
              View all
            </button>
          </div>
          {(!data?.topCreators || data.topCreators.length === 0) ? (
            <div className="text-center py-6">
              <Users className="w-8 h-8 text-white/8 mx-auto mb-2" />
              <p className="text-xs text-white">No creators yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.topCreators.map((creator, idx) => (
                <div
                  key={creator.id}
                  onClick={() => navigate(`/dashboard/creators/${creator.id}`)}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-white/3 border border-white hover:border-white hover:bg-white/5 cursor-pointer transition-all group"
                >
                  <span className="text-[10px] font-bold text-white w-4 text-center">{idx + 1}</span>
                  {creator.profileImage ? (
                    <img src={creator.profileImage} alt={creator.name} className="w-8 h-8 rounded-full object-cover ring-1 ring-white/10" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-sc-pink/15 border border-sc-pink/20 flex items-center justify-center text-sc-pink text-xs font-bold">
                      {creator.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate group-hover:text-sc-pink transition-colors">{creator.name}</p>
                    <p className="text-[11px] text-white">{creator.contentCount} pieces</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
